import {gmAPI, MapService} from '@/services/MapService';
import {Graphic} from '../Graphic.js';
import {SVGOverlayView} from '../SVGOverlayView';
import {SubscriptionManager} from '@/lib/filters/subscriptions';
import {geohashForLocation} from 'geofire-common';
import * as colors from '@/lib/colors';

/**
 * @typedef {google.maps.LatLngLiteral[]} Route
 *
 * @typedef {import('@/services/OverlaysService/Feature').Bounds} Bounds
 *
 * @typedef {object} AccessRouteData
 * @property {Route} route Array of all points on the route.
 * @property {google.maps.DirectionsRendererOptions} [options] Options for this graphic's Google Maps API objects.
 *
 * @typedef {import('../GraphicService').GraphicData & AccessRouteData} AccessRouteGraphicData
 * @typedef {import('@/lib/classes/InfoWindowContent').DisplayField} DisplayField
 */

const METERS_TO_MILES = 0.000621371;

/**
 * Gets the default icon for the draggable markers on the ends of an editable AccessRouteGraphic.
 * @return {google.maps.Icon}
 */
export function getAccessRouteMarkerIcon() {
  return {
    url: 'https://storage.googleapis.com/beyond_maps_public/Icons_Browser/Blue-Dot.png',
    scaledSize: new gmAPI.Size(16, 16),
    anchor: new gmAPI.Point(8, 8),
  };
}

/**
 * Gets the default renderer options for use in the AccessRouteGraphic.
 * @private
 * @return {google.maps.DirectionsRendererOptions}
 */
function getDefaultOptions() {
  /** @type {string} */
  return {
    draggable: true,
    preserveViewport: true,
    polylineOptions: {
      strokeColor: colors.blue,
      strokeWeight: 4,
      draggable: false,
      editable: false,
    },
    markerOptions: {
      visible: true,
      icon: getAccessRouteMarkerIcon(),
    },
  };
}

class AccessRouteGraphic extends Graphic {
  /**
   * @param {AccessRouteGraphicData} data
   */
  constructor(data) {
    super();
    // Options are nested objects and must be deeply cloned.
    const defaultOptions = getDefaultOptions();
    /** @type {google.maps.DirectionsRendererOptions} */
    this.options = {...defaultOptions, ...data?.options};
    this.options.polylineOptions = {...defaultOptions.polylineOptions, ...this.options.polylineOptions};
    this.options.markerOptions = {...defaultOptions.markerOptions, ...this.options.markerOptions};

    /** @type {string} */
    this.color = this.options.polylineOptions?.strokeColor ?? '#0098DA'; // Default to standard blue if no color is available

    /** @type {SVGOverlayView} */
    this.svgOverlay = new SVGOverlayView(this.options.polylineOptions, this.uuid, 'polyline');
    this.svgOverlay.setMap(MapService.getMap());

    /** @type {google.maps.DirectionsRenderer} */
    this.renderer = new gmAPI.DirectionsRenderer(this.options);
    /** @type {google.maps.TravelMode}  */
    this.travelMode = 'DRIVING';
    /** @type {boolean} */
    this.editMode = false;
    /** @type {boolean} */
    this.onMap = true;
    /** @type {SubscriptionManager} */
    this.subscriptions = new SubscriptionManager();
    /** @type {number} */
    this.distance = 0;
    /** @type {number} */
    this.distanceInMiles = 0;
    /** @type {number} */
    this.driveTime = 0;
    // Listen for changes in the route's directions and update subscribers.
    this.renderer.addListener('directions_changed', async () => {
      if (this.editMode) {
        // Update the route
        const directions = this.renderer.getDirections();
        if (directions) {
          this.calculateDistanceAndDriveTime(directions);
        }

        this.subscriptions.publish('graphic_changed', {
          recalcDisplayFields: [
            {
              label: 'Drive Time',
              value: this.driveTime,
            },
            {
              label: 'Distance',
              value: this.distanceInMiles,
            },
          ],
        });
      }
    });
    /**
     * @type {Promise<void>} Indicates if the constructor is currently waiting on the Directions API to turn the route
     * waypoints into directions
     */
    this.settingRoute = Promise.resolve();

    /** @type {Route} The route waypoints for rehydrating the Directions renderer */
    this.route = data?.route;

    /** @type {google.maps.LatLng[] | google.maps.LatLngLiteral[]} */
    this.path = typeof data?.path === 'string' ? gmAPI.geometry.encoding.decodePath(data.path) : data?.path;
    if (this.path?.length) {
      // if there is a path in the route data, set the SVG options immediately.
      this.svgOverlay.setOptions({path: this.path});
    } else if (this.route?.length) {
      // if there is no path, set the route now to get the SVG options.
      this.settingRoute = this.setRoute(this.route);
    }
  }

  /**
   * Overwrites the graphic's route with new values.
   * @private
   * @param {Route} route The route data.
   * @return {Promise<void>} A Promise resolving when the graphic is updated.
   */
  async setRoute(route) {
    if ((route?.length ?? 0) < 2) {
      console.warn('Trying to update AccessRouteGraphic route with fewer than two points; aborting.');
      return;
    }
    if (route.length > 25) {
      throw new Error(`Cannot set route with more than 25 points: ${route.length} points given.`);
    }
    try {
      const directions = await new gmAPI.DirectionsService().route({
        origin: route[0],
        destination: route.slice(-1)[0],
        waypoints: route.slice(1, -1).map((point) => ({location: point})),
        travelMode: this.travelMode,
      });
      this.renderer.setDirections(directions);
      this.path = directions.routes[0].overview_path;
      this.svgOverlay.setOptions({path: this.path});
      this.route = route;

      this.calculateDistanceAndDriveTime(directions);
    } catch (err) {
      if (err.code === 'ZERO_RESULTS') { // Suppress and just log a warning.
        console.warn(`AccessRouteGraphic.setRoute(): Google Maps could not find a route between these points: ${route}`);
      } else {
        throw err;
      }
    }
    this.subscriptions.publish('graphic_changed', {
      recalcDisplayFields: [
        {
          label: 'Drive Time',
          value: this.driveTime,
        },
        {
          label: 'Distance',
          value: this.distanceInMiles,
        },
      ],
    });
  }

  /** @param {boolean} enabled */
  setPointerEvents(enabled) {
    this.svgOverlay.setPointerEvents(enabled);
  }

  addHandler(eventType, handlerName, handler) {
    if (this.editMode) {
      console.warn('AccessRouteGraphic events cannot be tracked while the graphic is being edited.');
    }
    super.addHandler(eventType, handlerName, handler);
  }

  addLocalHandler(eventType, handlerName, handler) {
    // SVGOverlayView does not care about the handlerName.
    this.svgOverlay.addListener(eventType, handler);
  }

  removeLocalHandler(eventType, handlerName, handler) {
    // SVGOverlayView does not care about the handlerName.
    this.svgOverlay.removeListener(eventType, handler);
  }

  setClickable(clickable) {
    this.options.polylineOptions.clickable = clickable;
    this.options.markerOptions.clickable = clickable;
    this.renderer.setOptions(this.options);
  }

  isClickable() {
    return this.options.polylineOptions.clickable;
  }

  setMap(map) {
    (this.editMode ? this.renderer : this.svgOverlay).setMap(map);
    this.onMap = (map !== null);
  }

  subscribe(eventName, callback) {
    return this.subscriptions.addSubscription(eventName, callback);
  }

  /**
   * @return {google.maps.DirectionsRoute}
   * @throws {Error} 'NO_ROUTE' error if Google Maps Directions API did not find a route for this graphic.
   */
  getRoute() {
    const route = this.renderer.getDirections()?.routes?.[0];
    if (route === undefined) {
      const error = new Error('Google Maps API could not find a route between the points specified in the AccessRouteGraphic.');
      error.code = 'NO_ROUTE';
      throw error;
    }
    return route;
  }

  toJSON() {
    // set up variables
    const superJSON = super.toJSON();
    const options = {...this.options};
    const bounds = this.svgOverlay.bounds.toJSON();

    // delete marker options
    delete options.markerOptions;

    const result = {
      ...superJSON,
      bounds,
      geohash: geohashForLocation([(bounds.north + bounds.south) / 2, (bounds.east + bounds.west) / 2]),
      graphicData: {
        featureType: 'route',
        route: this.route,
        path: gmAPI.geometry.encoding.encodePath(this.path),
        options,
      },
    };
    return result;
  }

  getToolbarOptions() {
    return {
      toolbarOptions: [
        [
          {
            icon: 'Fill',
            action: 'colorPicker',
            name: 'Change route color',
            pickers: [
              {
                title: 'Route Color',
                getColor: () => this.color,
                setColor: (color) => {
                  this.color = color;
                  this.options.polylineOptions.strokeColor = color;
                  this.renderer.setOptions(this.options);
                  this.svgOverlay.setOptions(this.options.polylineOptions);
                  this.renderer.setMap(MapService.getMap()); // force visual update
                  this.subscriptions.publish('graphic_changed');
                },
                allowTransparent: false,
                availableColors: colors.overlayColorPalette,
              },
            ],
          },
        ],
      ],
    };
  }

  async setEditing(/** @type {boolean} */editing, /** @type {EditingContext} */context) {
    // Don't do anything if the setting isn't changing or if the graphic is not on the map.
    if (this.editMode === editing || this.onMap === false) return false;

    this.editMode = editing;

    // turn ON editing
    if (editing) {
      // The renderer is not fully rehydrated on construction, but it must be rehydrated before we can edit it.
      if (!this.renderer.getDirections()) {
        await this.setRoute(this.route);
      }

      // Polyline -> DirectionsRenderer
      this.svgOverlay.setMap(null);
      this.renderer.setMap(MapService.getMap());

      // Open the info window
      this.subscriptions.publish('open_info_window', {
        toolbar: this.getToolbarOptions(),
        featureType: 'route',
      });
      return false;
    }

    // turn OFF editing
    this.setRoute(getRoutePoints(this.getRoute()));
    // DirectionsRenderer -> Polyline
    this.svgOverlay.setMap(MapService.getMap());
    this.renderer.setMap(null);
    return false;
  }

  setHighlight(highlighted) {
    try {
      this.svgOverlay.setHighlighted(highlighted);
    } catch (err) {
      console.warn(err);
    }
  }

  setZIndex(zIndex) {
    this.svgOverlay.setZIndex(zIndex);
  }

  getLocation() {
    const midPoint = this.path[Math.floor(this.path.length / 2)];
    // if the point is a google.maps.LatLng, convert it to a google.maps.LatLngLiteral
    return midPoint.toJSON ? midPoint.toJSON() : midPoint;
  }

  calculateDistanceAndDriveTime(directions) {
    let totalDistance = 0;
    let totalDriveTime = 0;
    directions.routes[0].legs.forEach((leg) => {
      totalDistance += leg.distance.value;
      totalDriveTime += leg.duration.value;
    },
    );
    // Set Distance and DriveTime ("x hrs y mins")
    this.distance = totalDistance;
    this.distanceInMiles = totalDistance * METERS_TO_MILES;
    const driveTimeObj = toHoursAndMinutes(totalDriveTime);
    if (driveTimeObj.h <= 0) {
      this.driveTime = `${driveTimeObj.m} min`;
    } else {
      this.driveTime = `${driveTimeObj.h} hrs ${driveTimeObj.m} min`;
    }
  }
}

/**
 * @param {google.maps.DirectionsRoute} route
 * @return {Route}
 */
function getRoutePoints(route) {
  // This complexity is needed because when editing the route, waypoints are represented as elements of the
  // `via_waypoints` array within a leg; however, when rehydrating a route from Firestore, we get back multiple
  // legs with empty `via_waypoints` arrays. This seems to be an unavoidable quirk of the Google Maps API.
  return route.legs
    .flatMap((leg) => [
      leg.start_location,
      leg.via_waypoints,
      leg.end_location,
    ])
    .flat()
    .map((point) => point.toJSON());
}
/**
 * @param {number} totalSeconds total number of seconds
 * @return {{h: hours, m: minutes, s: seconds}}
 */
function toHoursAndMinutes(totalSeconds) {
  const totalMinutes = Math.floor(totalSeconds / 60);

  const seconds = totalSeconds % 60;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return {h: hours, m: minutes, s: seconds};
}


export {AccessRouteGraphic};
